import { useEffect, useMemo, useState } from 'react';

import * as Font from 'expo-font';

type FontsMap = Record<string, Font.FontSource>;

async function loadResourcesAndDataAsync(
  fontsToLoad: FontsMap,
  onLoaded: (fontsLoaded: FontsMap) => void
) {
  try {
    // Load cached resources
    await Font.loadAsync({
      ...(fontsToLoad || {}),
    });
  } catch (e) {
    // We might want to provide this error information to an error reporting service
    // eslint-disable-next-line no-console
    console.warn(e);
  } finally {
    onLoaded(fontsToLoad);
  }
}

// useCachedFonts is a hook that will return false once all of the fonts have loaded
//
// Example
//
//   import { bkTheme } from '../themes/bk';
//
//   const isNotLoaded = useCachedFonts(bkTheme.cachedFonts);
//

const EMPTY_FONTS_MAP = {};

export function useCachedFonts(cachedFonts: FontsMap = EMPTY_FONTS_MAP) {
  const [lastLoaded, setLastLoaded] = useState<FontsMap | null>(null);

  // Load any resources or data that we need prior to rendering the app
  useEffect(() => {
    if (lastLoaded !== cachedFonts) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadResourcesAndDataAsync(cachedFonts, setLastLoaded);
    }
  }, [cachedFonts, lastLoaded, setLastLoaded]);

  const areAllFontsLoaded = useMemo(() => {
    if (lastLoaded !== cachedFonts) {
      return false;
    }

    return Object.keys(cachedFonts).every(fontFamily => Font.isLoaded(fontFamily));
  }, [cachedFonts, lastLoaded]);

  return !areAllFontsLoaded;
}
